import React, { useEffect, useState } from "react";
import { sdh_fieldValues, type FieldValues } from "../api/sdhAPI";

/**
 * "Where do this field's values come from?" — the answer the reviewer could not previously get.
 * Shows what the data actually contains, what the pipeline inferred, what the client's own reference
 * extract supplies, and how the physical table is delivered. Three MATKL rules were rejected as
 * "no values in the raw data" when the field was in fact 100% populated and fully governed; this panel
 * exists so that question is answerable in one click.
 */
export default function FieldValuesPanel(
  { entityId, table, column, onClose, ruleCodes }:
  { entityId: string; table: string; column: string; onClose: () => void; ruleCodes?: string[] }
) {
  const [d, setD] = useState<FieldValues | null>(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      setBusy(true); setErr(null);
      try { const r = await sdh_fieldValues(entityId, table, column); if (live) setD(r); }
      catch (e: any) { if (live) setErr(e?.response?.data?.detail || e?.message || "Could not load values"); }
      finally { if (live) setBusy(false); }
    })();
    return () => { live = false; };
  }, [entityId, table, column]);

  const ob = d?.observed, s1 = d?.step1_profiled, s2 = d?.step2_client_supplied;
  const governed = s2?.values || [];
  const U = (x: any) => String(x ?? "").trim().toUpperCase();
  // ACCEPTED = the codes the RULE enforces (opened from a rule) - otherwise the reference's ACTIVE codes
  const hasGov = governed.some((g: any) => g.governance_status);
  // ACCEPTED = the codes the RULE enforces (opened from a rule) - else the codes IN EKD - else the ACTIVE codes
  const accSet = ruleCodes && ruleCodes.length ? new Set(ruleCodes.map(U))
    : hasGov ? new Set(governed.filter((g: any) => g.in_ekd).map((g) => U(g.code)))
    : new Set(governed.filter((g) => !g.status || g.status === "ACTIVE").map((g) => U(g.code)));
  const govOf: Record<string, string> = {};
  governed.forEach((g: any) => { if (g.governance_status) govOf[U(g.code)] = g.governance_status + (g.in_ekd ? " · in EKD" : ""); });
  const govSet = accSet;
  const obsSet = new Set(((d?.observed?.values) || []).map((v: any) => U(v.value)));
  const obsRows: Record<string, number> = {};
  ((d?.observed?.values) || []).forEach((v: any) => { obsRows[U(v.value)] = v.rows; });
  const groups: Record<string, any[]> = {};
  governed.forEach((g: any) => {
    // with governance: in EKD (used) | Approved but NOT in EKD (the gap) | by status; without: accepted | gap | status
    const k = accSet.has(U(g.code)) ? "ACCEPTED"
      : hasGov ? (g.governance_status === "Approved" ? "GAP" : String(g.governance_status || "Not set"))
      : (!g.status || g.status === "ACTIVE") ? "GAP" : String(g.status);
    (groups[k] = groups[k] || []).push(g);
  });
  const tabs: Array<[string, string]> = [["ACCEPTED", ruleCodes?.length ? "In EKD · used by the rule" : hasGov ? "In EKD" : "Accepted"],
    ["GAP", hasGov ? "Approved · not in EKD" : ruleCodes?.length ? "Active · not in the rule" : "Not accepted"],
    ...Object.keys(groups).filter((k) => k !== "ACCEPTED" && k !== "GAP").sort().map((k) => [k, k.charAt(0) + k.slice(1).toLowerCase()] as [string, string])];
  const [tab, setTab] = useState<"ACC" | "REST">("ACC");
  const [shut, setShut] = useState<Record<string, boolean>>({});
  const ORDER = ["GAP", "Draft", "Submitted For Approval", "Candidate", "Not Documented Yet", "Not set", "Obsolete"];
  const LABEL: Record<string, string> = { GAP: "Approved · not in EKD" };
  const restKeys = Object.keys(groups).filter((k) => k !== "ACCEPTED" && (groups[k] || []).length)
    .sort((a, b) => (ORDER.indexOf(a) < 0 ? 99 : ORDER.indexOf(a)) - (ORDER.indexOf(b) < 0 ? 99 : ORDER.indexOf(b)));
  const restCount = restKeys.reduce((n, k) => n + groups[k].length, 0);
  const rowsOf = (g: any) => Number(obsRows[U(g.code)] || 0);
  const byUse = (a: any, b: any) => rowsOf(b) - rowsOf(a) || String(a.code).localeCompare(String(b.code));
  const shortSt = (code: string) => { const g = govOf[U(code)]; if (!g) return govSet.size ? "not in reference" : "";
    return g.replace(" · in EKD", "").replace("Not Documented Yet", "Not documented").replace("Submitted For Approval", "Submitted"); };
  const notAccepted = ((d?.observed?.values) || []).filter((v: any) => v.value !== "" && !accSet.has(U(v.value)));

  return (
    <div className="fv-scrim" onClick={onClose}>
      <div className="fv" onClick={(e) => e.stopPropagation()}>
        <style>{CSS}</style>
        <header className="fv-h">
          <div>
            <div className="eyebrow">FIELD VALUES</div>
            <div className="title">{table}.{column}</div>
          </div>
          <button className="x" onClick={onClose}>×</button>
        </header>

        <div className="fv-body">
          {busy && <div className="mut">Loading…</div>}
          {err && <div className="fv-err">{err}</div>}

          {d && (
            <>
              {d.grain && (
                <div className="grain">
                  <b>How this table is delivered:</b> {d.grain.grain}
                  {d.grain.note && <div className="n">{d.grain.note}</div>}
                </div>
              )}

              {(d.reconciliation || []).length > 0 && (
                <ul className="recon">
                  {d.reconciliation!.map((line, i) => <li key={i}>{line}</li>)}
                </ul>
              )}

              <div className="cols">
                {/* what the data really has */}
                <section>
                  <div className="sh">In your data
                    {ob && <span className="c">{ob.distinct_count} distinct · {ob.null_pct}% empty</span>}
                  </div>
                  {!ob ? <div className="mut">Not profiled.</div> : (
                    <div className="vals">
                      {(ob.values || []).map((v) => {
                        const known = govSet.size === 0 || govSet.has(String(v.value).trim().toUpperCase());
                        return (
                          <div className={"v" + (known ? "" : " off")} key={v.value}>
                            <span className="k">{v.value === "" ? "(blank)" : v.value}</span>
                            <span className="n">{v.rows.toLocaleString()}</span>
                            {v.pct != null && <span className="p">{v.pct}%</span>}
                            {known ? <span className="okm" title="Accepted">✓</span>
                              : <span className={"stl " + shortSt(v.value).replace(/\s+/g, "").toLowerCase()} title="Not accepted - every row with this value fails; its status in Reference Data">{shortSt(v.value) || "not accepted"}</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>

                {/* what the client supplied */}
                <section>
                  <div className="sh">From your reference data
                    {s2 && <span className="c">{governed.length} code(s)</span>}
                  </div>
                  {s2 && s2.has_status_information === false && (
                    <div className="nostatus">
                      <b>No value status supplied.</b> This reference has no Active/Obsolete column, so a
                      retired code cannot be told apart from a valid one — and rules will treat every code
                      as enforceable.
                      <div className="n">Re-upload the reference including its status column on the
                        Reference Data page.</div>
                    </div>
                  )}
                  {!s2 ? <div className="mut">This column is not mapped in the reference extract.</div>
                    : governed.length === 0 ? (
                      <div className="gap">
                        Mapped to <b>{s2.mapped_logical_field}</b>, but no governed codes are available
                        {s2.domain_present_in_client_extract === false && " — that domain was not in the file you supplied"}.
                        <div className="n">Load it on the Reference Data page and value rules will bind to it.</div>
                      </div>
                    ) : (
                      <>
                        <div className="fv-tabs">
                          <button className={tab === "ACC" ? "on" : ""} onClick={() => setTab("ACC")}>Accepted <i>{(groups.ACCEPTED || []).length}</i></button>
                          <button className={tab === "REST" ? "on" : ""} onClick={() => setTab("REST")}>Rest <i>{restCount}</i></button>
                          <span className="fv-tabn">{tab === "ACC" ? (ruleCodes?.length ? "what the rule enforces (in EKD)" : hasGov ? "in Enterprise Knowledge" : "active codes") : "not enforced - grouped by status"}</span>
                        </div>
                        <div className="fv-ref">
                          <div className="fv-rh"><span>Code</span><span>Meaning</span><span>Rows in your data</span></div>
                          {tab === "ACC" ? ((groups.ACCEPTED || []).length === 0 ? <div className="mut fv-pad">None.</div>
                            : [...groups.ACCEPTED].sort(byUse).map((g: any) => <RefRow key={g.code} g={g} rows={rowsOf(g)} />))
                          : restKeys.length === 0 ? <div className="mut fv-pad">None.</div> : restKeys.map((k) => {
                              const list = [...groups[k]].sort(byUse); const used = list.filter((g) => rowsOf(g) > 0);
                              const open = shut[k] === undefined ? used.length > 0 : !shut[k];
                              return <div key={k} className="fv-grp">
                                <button className="fv-gh" onClick={() => setShut({ ...shut, [k]: open })}>
                                  <span className="car">{open ? "▾" : "▸"}</span><b>{LABEL[k] || k}</b><span className="mut">{list.length} code(s)</span>
                                  {used.length > 0 && <span className="use">{used.length} used in your data · {used.reduce((n, g) => n + rowsOf(g), 0).toLocaleString()} rows</span>}
                                </button>
                                {open && list.map((g: any) => <RefRow key={g.code} g={g} rows={rowsOf(g)} />)}
                              </div>; })}
                        </div>
                        {notAccepted.length > 0 && (
                          <div className="fv-fix">
                            <b>{notAccepted.length} value(s) in your data are not accepted</b>
                            {ruleCodes?.length ? " by this rule" : ""}: {notAccepted.slice(0, 8).map((v: any) => v.value + (govOf[U(v.value)] ? ` (${govOf[U(v.value)]})` : "")).join(", ")}{notAccepted.length > 8 ? " …" : ""} —
                            {" "}{notAccepted.reduce((a: number, v: any) => a + (v.rows || 0), 0).toLocaleString()} rows.
                            <div className="n">If some are valid: 1. Reference Data page → set their status to Approved · 2. promote / sync them to Enterprise Knowledge ·
                              3. refine this rule (Refine with agent) or regenerate it with the new list.</div>
                          </div>)}
                      </>
                    )}
                  {s1 && (
                    <div className="s1">
                      <b>Pipeline-inferred list:</b> {(s1.values || []).length} value(s)
                      {s1.status ? ` · ${s1.status}` : ""}
                      {s1.reviewed_by ? ` by ${s1.reviewed_by}` : ""}
                      {s1.reviewed_at ? ` on ${String(s1.reviewed_at).slice(0, 10)}` : ""}
                      {s1.rationale && <div className="n">{s1.rationale}</div>}
                    </div>
                  )}
                </section>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const CSS = `
.fv-scrim{position:fixed;inset:0;z-index:92;background:rgba(14,36,54,.42);display:flex;align-items:center;justify-content:center;padding:24px}
.fv{--line:#e3e8ee;--muted:#6b7c8d;width:min(960px,96vw);max-height:90vh;background:#fff;border-radius:16px;
 display:flex;flex-direction:column;overflow:hidden;box-shadow:0 28px 70px rgba(0,0,0,.3);color:#0E2436}
.fv-h{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;background:#0e2436;color:#fff}
.fv-h .eyebrow{font-size:10px;letter-spacing:.12em;color:#9db4c9}
.fv-h .title{font-size:16px;font-weight:700;font-family:ui-monospace,monospace}
.fv-h .x{all:unset;cursor:pointer;font-size:24px;color:#cdd8ea}.fv-h .x:hover{color:#fff}
.fv-body{overflow:auto;padding:16px 20px 20px}
.mut{color:var(--muted);font-size:13px}
.fv-err{background:#fdeceb;color:#b23a30;border-radius:8px;padding:9px 12px;font-size:12.5px}
.grain{background:#f6f8fa;border-left:3px solid #43607e;border-radius:8px;padding:9px 12px;font-size:12.5px;margin-bottom:10px}
.grain .n{color:var(--muted);margin-top:4px;line-height:1.5}
.recon{margin:0 0 14px;padding-left:18px;font-size:13px;color:#33404e;line-height:1.6}
.cols{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.cols .sh{display:flex;align-items:baseline;gap:8px;font-size:11px;letter-spacing:.07em;text-transform:uppercase;
 color:#9aa6b2;font-weight:700;margin-bottom:7px}
.cols .sh .c{font-size:11px;color:var(--muted);text-transform:none;letter-spacing:0;font-weight:400}
.vals{border:1px solid var(--line);border-radius:9px;max-height:320px;overflow:auto}
.vals .v{display:flex;align-items:center;gap:9px;padding:5px 10px;border-bottom:1px solid #eef1f4;font-size:12.5px}
.vals .v:last-child{border-bottom:0}
.vals .v.off{background:#fdf6f5}
.vals .k{font-family:ui-monospace,monospace;font-weight:600}
.vals .l{color:var(--muted);font-size:11.5px}
.vals .n{margin-left:auto;font-family:ui-monospace,monospace;color:#42586e}
.vals .p{font-size:11px;color:var(--muted);width:44px;text-align:right}
.vals .warn{color:#b23a30;font-weight:700}
.fv-tabs{display:flex;gap:4px;flex-wrap:wrap;margin:2px 0 8px}.fv-tabs button{border:1px solid #e3e6ea;background:#fff;border-radius:14px;padding:3px 10px;cursor:pointer;font:inherit;font-size:12px}
.fv-tabs button.on{background:#7500C0;border-color:#7500C0;color:#fff}.fv-tabs i{font-style:normal;opacity:.75;margin-left:3px}
.v.gap{background:#fff7f7}.st.gov{border:1px solid #e3e6ea;background:#f6f7f9;color:#4b5563}.st.gov.approved{background:#ecfdf5;color:#047857;border-color:#a7f3d0}
.st.gov.draft{background:#fffbeb;color:#92400e;border-color:#fde68a}.st.gov.obsolete{background:#f3f4f6;color:#6b7280;text-decoration:line-through}
.st.gov.submittedforapproval,.st.gov.candidate{background:#eff6ff;color:#1d4ed8;border-color:#bfdbfe}.st.indata{background:#fde8e6;color:#9b2b1f;border-color:#f3c2bb}
.fv-fix{margin-top:8px;background:#fbf6ea;border:1px solid #ead9b4;border-radius:9px;padding:9px 12px;font-size:12.5px;color:#5c4a1f}.fv-fix .n{margin-top:4px;color:#9a7b3a}
.fv-tabn{font-size:11.5px;color:#6b7280;margin-left:6px;align-self:center}
.fv-ref{border:1px solid #e6e8ec;border-radius:9px;max-height:300px;overflow:auto;font-size:12.5px}
.fv-rh{display:grid;grid-template-columns:64px 1fr 120px;gap:8px;padding:6px 10px;background:#f9fafb;color:#6b7280;font-size:11px;font-weight:600;position:sticky;top:0;z-index:1}
.fv-rh span:last-child,.fv-rr .n{text-align:right}
.fv-rr{display:grid;grid-template-columns:64px 1fr 120px;gap:8px;padding:5px 10px;border-top:1px solid #f3f4f6;align-items:center}
.fv-rr .k{font-family:monospace;font-weight:600}.fv-rr .l{color:#374151;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.fv-rr .n{color:#9ca3af;font-variant-numeric:tabular-nums}.fv-rr.used{background:#fffaf5}.fv-rr.used .n{color:#b45309;font-weight:600}
.fv-grp+.fv-grp{border-top:1px solid #e6e8ec}.fv-gh{all:unset;box-sizing:border-box;width:100%;cursor:pointer;display:flex;gap:8px;align-items:center;padding:7px 10px;background:#fcfcfd}
.fv-gh:hover{background:#f5f0ff}.fv-gh .car{color:#7500C0;width:10px}.fv-gh .use{margin-left:auto;color:#b45309;font-size:11.5px;font-weight:600}
.fv-pad{padding:10px}.okm{color:#047857;font-weight:700;margin-left:6px}
.stl{margin-left:6px;font-size:10.5px;font-weight:600;border-radius:8px;padding:1px 7px;border:1px solid #e3e6ea;background:#f6f7f9;color:#4b5563;white-space:nowrap}
.stl.draft{background:#fffbeb;color:#92400e;border-color:#fde68a}.stl.notdocumented{background:#f3f4f6;color:#4b5563}
.stl.submitted,.stl.candidate{background:#eff6ff;color:#1d4ed8;border-color:#bfdbfe}.stl.notinreference,.stl.notaccepted{background:#fde8e6;color:#9b2b1f;border-color:#f3c2bb}
.stl.obsolete{background:#f3f4f6;color:#6b7280}.stl.approved{background:#ecfdf5;color:#047857;border-color:#a7f3d0}
.nostatus{background:#fbf6ea;border:1px solid #ead9b4;border-radius:9px;padding:10px 12px;font-size:12.5px;
 color:#8a6516;line-height:1.5;margin-bottom:9px}
.nostatus .n{margin-top:5px;color:#9a7b3a}
.vals .v.dead{background:#fdf6f5;opacity:.85}
.vals .v.dead .k{text-decoration:line-through}
.vals .st{margin-left:auto;font-size:10px;font-weight:700;letter-spacing:.05em;color:#b23a30;background:#fbe9e7;border-radius:20px;padding:1px 8px}
.gap{background:#fbf6ea;border:1px solid #ead9b4;border-radius:9px;padding:10px 12px;font-size:12.5px;color:#8a6516;line-height:1.5}
.gap .n{margin-top:5px;color:#9a7b3a}
.s1{margin-top:10px;font-size:12px;color:#4a5763;background:#f8fafb;border-radius:8px;padding:8px 11px;line-height:1.5}
.s1 .n{color:var(--muted);margin-top:4px}
@media(max-width:820px){.cols{grid-template-columns:1fr}}
`;

function RefRow({ g, rows }: { g: any; rows: number; key?: any }) {
  return <div className={"fv-rr" + (rows > 0 ? " used" : "")} title={g.description || ""}>
    <span className="k">{g.code}</span><span className="l">{g.label || "—"}</span>
    <span className="n">{rows > 0 ? rows.toLocaleString() : "—"}</span></div>;
}
